<template>
<div class="simple-page">
  <HeadNav selectedLanguage="en" page="privacy" />

  <main class="simple-page__main">
    <h1>Privacy Policy</h1>

    <p><strong>Effective Date: September 11, 2026</strong></p>
    <p><strong>Last Updated: September 14, 2026</strong></p>

    <p>
      At <strong>Secure VPN</strong>, we respect your privacy and are committed to protecting your personal information.
      This Privacy Policy explains how we collect, use, and process data when you visit our website and download our
      application via official app stores (such as the Google Play Store).
    </p>

    <section>
      <h2>1. Strict No-Logs Policy for VPN Services</h2>
      <p>
        We enforce a strict <strong>No-Logs Policy</strong> to protect your online privacy. When you use the Secure VPN
        service, we do <strong>NOT</strong> collect, store, or log:
      </p>
      <ul>
        <li>Your IP address or assigned VPN IP address.</li>
        <li>Your browsing history, visited websites, or online searches.</li>
        <li>Your connection timestamps, session durations, or traffic contents.</li>
        <li>Your DNS queries or communications data.</li>
      </ul>
    </section>

    <section>
      <h2>2. Information We Collect</h2>
      <p>
        To maintain website functionality, deliver fast page loads, and analyze general traffic performance, we may collect
        limited, non-identifiable technical information automatically when you visit our website:
      </p>
      <ul>
        <li><strong>Browser & Device Information:</strong> Device type, operating system, and browser version.</li>
        <li><strong>Usage Data:</strong> Pages visited, time spent on pages, and referring URLs.</li>
        <li><strong>Aggregated Technical Metrics:</strong> Anonymized speed, latency, and crash report data used strictly for system optimizations.</li>
      </ul>
    </section>

    <section>
      <h2>3. Cookies and Tracking Technologies</h2>
      <p>
        Secure VPN uses standard cookies and similar tracking technologies to improve website functionality and understand
        how visitors interact with our content.
      </p>
      <ul>
        <li><strong>Essential Cookies:</strong> Required for basic website operations and security features.</li>
        <li><strong>Analytics Cookies:</strong> Used to measure aggregate traffic metrics and website performance (e.g., via Google Analytics).</li>
        <li><strong>Managing Cookies:</strong> You can adjust your browser settings to decline or delete cookies at any time. Disabling cookies will not block your access to our website or app download links.</li>
      </ul>
    </section>

    <section>
      <h2>4. Google Ads and Third-Party Advertising</h2>
      <p>
        We may partner with third-party advertising networks, including <strong>Google Ads</strong>, to display
        advertisements or measure marketing campaign effectiveness across the internet.
      </p>
      <ul>
        <li>Third-party vendors, including Google, use cookies and/or device identifiers to serve ads based on someone's past visits to our website.</li>
        <li><strong>Opting Out of Personalized Ads:</strong> Visitors may opt out of personalized advertising by visiting <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer">Google Ads Settings</a> or by managing cookie choices through your browser settings.</li>
      </ul>
    </section>

    <section>
      <h2>5. Official App Downloads & Third-Party Platforms</h2>
      <p>
        Our website provides links and buttons that redirect users to official app platforms (primarily the
        <strong>Google Play Store</strong>) to complete application downloads securely.
      </p>
      <p>
        Once you leave our website and navigate to Google Play, any app downloads, installations, or account interactions
        are governed by <strong>Google's Privacy Policy</strong> and Terms of Service. We do not collect payment card
        information or sensitive store account credentials on this website.
      </p>
    </section>

    <section>
      <h2>6. Data Security</h2>
      <p>
        We implement industry-standard security measures to safeguard collected information and maintain a secure browsing
        environment. While we continuously strive to protect your data, please note that no internet transmission method
        can offer absolute security.
      </p>
    </section>

    <section>
      <h2>7. Policy Updates</h2>
      <p>
        We may update this Privacy Policy periodically to reflect changes in our legal requirements or operational
        practices. Any changes will be posted on this page with an updated "Last Updated" date.
      </p>
    </section>

    <section>
      <h2>8. Contact Us</h2>
      <p>
        If you have any questions or concerns regarding this Privacy Policy or our data practices, please reach out to us at:
      </p>
      <ul>
        <li><strong>Email:</strong> <a href="mailto:support@secureproxyvpn.com">support@secureproxyvpn.com</a></li>
      </ul>
    </section>
  </main>
</div>
</template>

<script>
import '@/css/vpn-theme.scss';
import HeadNav from '@/components/HeadNav.vue';

export default {
  name: 'PrivacyPolicy',
  components: {
    HeadNav,
  },
  mounted() {
    if (typeof document !== 'undefined') {
      document.body.classList.add('vpn-site');
    }
  },
};
</script>

<style lang="scss" scoped>
.simple-page {
  min-height: 70vh;
  background: #fff;
}

.simple-page__main {
  width: var(--vpn-content-width);
  margin: 0 auto;
  padding: 56px 0 72px;
  color: var(--vpn-ink-muted, #5b6475);
}

h1 {
  margin: 0 0 32px;
  color: var(--vpn-ink, #1f2430);
  font-size: 42px;
  line-height: 1.15;
  font-weight: 800;
}

section {
  margin: 0 0 34px;
}

h2 {
  margin: 0 0 14px;
  color: var(--vpn-ink, #1f2430);
  font-size: 26px;
  line-height: 1.25;
  font-weight: 800;
}

p {
  margin: 0 0 14px;
  font-size: 16px;
  line-height: 1.75;
}

ul {
  margin: 0 0 18px 22px;
  padding: 0;
}

li {
  margin: 0 0 8px;
  font-size: 16px;
  line-height: 1.65;
}

a {
  color: var(--vpn-brand, #6d4aff);
  font-weight: 700;
  text-decoration: none;
}

a:hover {
  text-decoration: underline;
}

@media (max-width: 768px) {
  .simple-page__main {
    width: 100%;
    padding: 40px 16px 56px;
    box-sizing: border-box;
  }

  h1 {
    font-size: 34px;
  }

  h2 {
    font-size: 23px;
  }
}
</style>
